// src/pages/Contact.js
import React, { useState } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { contactInfo } from '../data/apartments';

const ContactContainer = styled.div`
  padding: 6rem 0 3rem;
  background: white;
  min-height: 100vh;
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 3rem;

  h1 {
    font-size: 3rem;
    color: ${props => props.theme.colors.secondary};
    margin-bottom: 1rem;
  }

  p {
    font-size: 1.2rem;
    color: ${props => props.theme.colors.gray};
  }
`;

const ContactInfo = styled.div`
  max-width: 1000px;
  margin: 0 auto 4rem;

  h2 {
    font-size: 2rem;
    margin-bottom: 2rem;
    color: ${props => props.theme.colors.secondary};
    text-align: center;
  }
`;

const ContactGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 2rem;

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    grid-template-columns: 1fr;
  }
`;

const ContactItem = styled(motion.div)`
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  padding: 1.5rem;
  background: ${props => props.theme.colors.light};
  border-radius: ${props => props.theme.borderRadius.large};
  transition: transform 0.2s ease;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 10px 30px rgba(0,0,0,0.1);
  }

  .icon {
    font-size: 2rem;
    min-width: 50px;
    text-align: center;
  }

  .details {
    flex: 1;

    h3 {
      margin-bottom: 0.5rem;
      color: ${props => props.theme.colors.secondary};
      font-size: 1.1rem;
    }

    p {
      margin-bottom: 0.3rem;
      color: ${props => props.theme.colors.secondary};
      font-weight: 500;
    }

    a {
      color: ${props => props.theme.colors.primary};
      text-decoration: none;

      &:hover {
        color: ${props => props.theme.colors.secondary};
      }
    }

    small {
      color: ${props => props.theme.colors.gray};
      display: block;
      margin-top: 0.3rem;
    }
  }
`;

const FAQ = styled.div`
  margin-top: 4rem;

  h2 {
    text-align: center;
    margin-bottom: 2.5rem;
    color: ${props => props.theme.colors.secondary};
    font-size: 2rem;
  }
`;

const FAQList = styled.div`
  max-width: 900px;
  margin: 0 auto;
`;

const FAQItem = styled.div`
  background: ${props => props.theme.colors.light};
  border-radius: ${props => props.theme.borderRadius.large};
  margin-bottom: 1.5rem;
  overflow: hidden;
  border: 2px solid transparent;

  ${props => props.$isOpen && `
    border-color: ${props.theme.colors.primary};
  `}
`;

const FAQQuestion = styled.button`
  width: 100%;
  padding: 1.5rem;
  background: none;
  border: none;
  text-align: left;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;

  h3 {
    color: ${props => props.theme.colors.secondary};
    font-size: 1.1rem;
    font-weight: 600;
    margin: 0;
  }

  .icon {
    font-size: 1.5rem;
    color: ${props => props.theme.colors.primary};
    transition: transform 0.2s ease;

    ${props => props.$isOpen && `
      transform: rotate(180deg);
    `}
  }

  &:hover h3 {
    color: ${props => props.theme.colors.primary};
  }
`;

const FAQAnswer = styled(motion.div)`
  overflow: hidden;

  .content {
    padding: 0 1.5rem 1.5rem;

    p {
      color: ${props => props.theme.colors.gray};
      line-height: 1.8;
      margin: 0;
    }
  }
`;

const faqData = [
  {
    question: 'Jak dokonać rezerwacji?',
    answer: 'Skontaktuj się z nami telefonicznie lub mailowo. Prześlemy Ci szczegóły dotyczące dostępności i procesu rezerwacji.'
  },
  {
    question: 'Czy są miejsca parkingowe?',
    answer: 'Tak, oba apartamenty posiadają miejsce parkingowe na ogrodzonym terenie osiedla.'
  },
  {
    question: 'Jaka jest minimalna długość pobytu?',
    answer: 'Minimalna długość pobytu to 1 noc. Dla dłuższych pobytów oferujemy atrakcyjne rabaty.'
  },
  {
    question: 'Czy można anulować rezerwację?',
    answer: 'Szczegóły dotyczące anulowania rezerwacji omówimy podczas kontaktu. Staramy się być elastyczni.'
  },
  {
    question: 'O której godzinie jest zameldowanie i wymeldowanie?',
    answer: 'Zameldowanie od 15:00, wymeldowanie do 11:00. W miarę możliwości możemy dostosować godziny do Twoich potrzeb.'
  },
  {
    question: 'Czy można otrzymać fakturę?',
    answer: 'Tak, istnieje możliwość wystawienia faktury za pobyt. Wystarczy podać dane do faktury podczas rezerwacji.'
  }
];

const Contact = () => {
  const [openFAQ, setOpenFAQ] = useState(null);

  return (
    <ContactContainer>
      <Container>
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >

          <Header>
            <h1>Kontakt</h1>
            <p>Skontaktuj się z nami, aby zarezerwować apartament lub uzyskać więcej informacji</p>
          </Header>

          <ContactInfo>
            <h2>Dane kontaktowe</h2>

            <ContactGrid>

              <ContactItem
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
              >
                <div className="icon">📞</div>
                <div className="details">
                  <h3>Telefon</h3>
                  <p><a href="tel:+48600761250">+48 600 761 250</a></p>
                </div>
              </ContactItem>

              <ContactItem
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.1 }}
              >
                <div className="icon">📧</div>
                <div className="details">
                  <h3>Email</h3>
                  <p><a href={`mailto:${contactInfo.email}`}>{contactInfo.email}</a></p>
                </div>
              </ContactItem>

            </ContactGrid>
          </ContactInfo>

          <FAQ>
            <h2>Często zadawane pytania</h2>

            <FAQList>
              {faqData.map((faq, index) => (
                <FAQItem key={index} $isOpen={openFAQ === index}>

                  <FAQQuestion
                    onClick={() => setOpenFAQ(openFAQ === index ? null : index)}
                    $isOpen={openFAQ === index}
                  >
                    <h3>{faq.question}</h3>
                    <span className="icon">▼</span>
                  </FAQQuestion>

                  <AnimatePresence>
                    {openFAQ === index && (
                      <FAQAnswer
                        initial={{ height: 0 }}
                        animate={{ height: 'auto' }}
                        exit={{ height: 0 }}
                        transition={{ duration: 0.2 }}
                      >
                        <div className="content">
                          <p>{faq.answer}</p>
                        </div>
                      </FAQAnswer>
                    )}
                  </AnimatePresence>

                </FAQItem>
              ))}
            </FAQList>

          </FAQ>

        </motion.div>
      </Container>
    </ContactContainer>
  );
};

export default Contact;